/**
 * localStorage behind a guard: a browser that refuses storage (private
 * window, quota, blocked site data) falls back to memory for the page, and
 * a comfort key (language, theme, sidebar, recent searches, API statistics)
 * reaches the disk only once the person agreed in the cookie dialog
 * (`utils/cookieConsent`). Until then, or after a refusal, it lives in memory
 * too, so the current page keeps its settings and nothing survives the tab.
 */
import { COMFORT_KEYS, isComfortKey, onConsentChange, preferencesAllowed } from './cookieConsent';

const memoryFallback = new Map<string, string>();
let useMemoryFallback = false;

function probeOnce(): boolean {
  if (typeof window === 'undefined' || !window.localStorage) {
    useMemoryFallback = true;
    return false;
  }
  try {
    const probeKey = '__safeStorage_probe__';
    window.localStorage.setItem(probeKey, '1');
    window.localStorage.removeItem(probeKey);
    return true;
  } catch {
    useMemoryFallback = true;
    return false;
  }
}

probeOnce();

/** Whether this key may be written to disk right now. */
const persisted = (key: string): boolean => !isComfortKey(key) || preferencesAllowed();

export const safeStorage = {
  getItem(key: string): string | null {
    if (useMemoryFallback) return memoryFallback.get(key) ?? null;
    try {
      return window.localStorage.getItem(key) ?? memoryFallback.get(key) ?? null;
    } catch {
      useMemoryFallback = true;
      return memoryFallback.get(key) ?? null;
    }
  },
  setItem(key: string, value: string): void {
    if (useMemoryFallback || !persisted(key)) {
      memoryFallback.set(key, value);
      return;
    }
    try {
      window.localStorage.setItem(key, value);
      memoryFallback.delete(key);
    } catch {
      useMemoryFallback = true;
      memoryFallback.set(key, value);
    }
  },
  removeItem(key: string): void {
    memoryFallback.delete(key);
    if (useMemoryFallback) return;
    try {
      window.localStorage.removeItem(key);
    } catch {
      useMemoryFallback = true;
    }
  },
};

// When the person agrees, the comfort settings chosen so far move to disk;
// when they refuse, those on disk move back to memory and leave the disk.
onConsentChange((consent) => {
  if (useMemoryFallback) return;
  try {
    for (const key of COMFORT_KEYS) {
      if (consent?.preferences) {
        const held = memoryFallback.get(key);
        if (held !== undefined) {
          window.localStorage.setItem(key, held);
          memoryFallback.delete(key);
        }
      } else {
        const stored = window.localStorage.getItem(key);
        if (stored !== null) {
          memoryFallback.set(key, stored);
          window.localStorage.removeItem(key);
        }
      }
    }
  } catch {
    useMemoryFallback = true;
  }
});
